import * as amplitude from "@amplitude/analytics-browser";
import { sessionReplayPlugin } from "@amplitude/plugin-session-replay-browser";
import cuid from "cuid";

import type { DeviceRegistration } from "@storyboard/web-client";
import { createLogger } from "@/logger";

const log = createLogger("warmup");

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react";
import { useParams } from "react-router-dom";

import {
  bootstrapWorkspaceDriverTranscripts,
  getLocations,
  getWorkspaceInvitations,
  registerDevice,
  serverNow,
} from "@storyboard/web-client";
import BootstrapFailedModal from "@/components/BootstrapFailedModal";
import { transport } from "@/config";
import { getWorkspaceConfig, subscribeWorkspaceConfigs, workspacesDelivered } from "@/data/workspaceConfig";
import { useGlobalFlags } from "@/hooks/useGlobalFlags";
import { useSafeLocalStorage } from "@/hooks/useSafeLocalStorage";
import UseStorage from "@/hooks/useStorage";
import { subscribeToAppSync } from "@/models/AppsyncHandler.ts";
import { startAcknowledgementSyncInBrowser } from "@/models/acknowledgement/acknowledgementSync";
import { loadWorkspaceWorkflowItems } from "@/models/actions/workflows";
import { appContext, deviceContext } from "@/models/apiContext";
import { syncWorkspaceMembershipAvailabilities } from "@/models/availabilitySync";
import useBootstrap, { ColdBootstrapFailedError } from "@/models/BootstrapApplication";
import {
  BOOTSTRAP_MAX_PAGE_SIZE,
  bootstrappedApiCalls,
  fetchLatestBootstrapEventDate,
  insertBootstrapEvent,
} from "@/models/BootstrapUtils.ts";
import { startCurrentRoutesPollingInBrowser } from "@/models/currentRoutes/currentRoutesPoller";
import { syncCurrentRoutes } from "@/models/currentRoutes/currentRoutesSync";
import { setPreferredLanguage as setPreferredLanguageRepo } from "@/models/repositories/Account";
import { bulkUpsertDirectWsInvitation } from "@/models/repositories/DirectWsInvitation";
import { bulkUpsertFile } from "@/models/repositories/File";
import { bulkUpsertItemAndCalculateMembership, resetAllItemsLoadedContent } from "@/models/repositories/Item";
import { bulkUpsertWsTranscriptions } from "@/models/repositories/Transcription";
import { UserContext } from "@/models/StateProviders/userProvider";
import { startTagPolling, stopTagPolling } from "@/models/tagPoller";
import { startTaskSyncInBrowser } from "@/models/task/taskSync";
import { startTmsEventPollingInBrowser } from "@/models/tmsEvent/tmsEventPoller";
import { bootstrapTmsEventStreams } from "@/models/tmsEvent/tmsEventSync";
import { abandonBootRun, BOOT_MANIFEST, runBootTask, skipBootTask, startBootRun } from "@/store/bootRun";
import { idx, rowsByIndex } from "@/store/store";
import type { Permission, WorkspaceMembership } from "@/store/types";
import { buildVersion, type MappedWorkspaceRole } from "@/utils";
import { notificationPermission } from "@/utils/notifications";
import { AppContext } from "./AppStateProvider";
import { warmupFeedItems } from "./actions/feedWarmup";
import { downloadPaginatedFeedPermissions } from "./actions/initialFeedLoad";
import { warmupSettingsFor } from "./actions/warmupConstants";
import { LANGUAGE_LIST, languageFamily, type PreferredLanguage } from "./languages";
import { CurrentFeedContext } from "./StateProviders/currentFeedProvider";
import { MyAccountContext } from "./StateProviders/myAccountProvider";
import { WorkspaceContext } from "./StateProviders/workspaceProvider";
import { TelemetryContext } from "./TelemetryProvider";

declare const window: Window & { dataLayer: Record<string, unknown>[] };

const LANGUAGE_SESSION_KEY = "preferredLanguage";
const LANGUAGE_DEFAULT_VALUE = LANGUAGE_LIST[0] ?? "none";

type DataState = {
  invalidWorkspace?: boolean;
  setInvalidWorkspace?: (value: boolean) => void;
  availableWorkspaceRoles?: Map<string, MappedWorkspaceRole>;
  bootstrapComplete?: boolean;
  /** A cold bootstrap failed; the view shows the failure and offers a fresh boot, and nothing new starts. */
  bootstrapFailed?: boolean;
  firstBootWorkspaceId?: string;
  loadWorkspaceWorkflowItems?: (params: { workspaceId: string; myAccountId: string }) => Promise<void>;
  preferredLanguage: PreferredLanguage;
  setPreferredLanguage?: (language: PreferredLanguage) => void;
  fetchWorkspaceMembership?: (workspaceMembershipId: string, feedId: string) => Promise<WorkspaceMembership>;
  listFeedPermissions?: (workspaceMembershipId: string, feedId: string) => Promise<Array<Permission>>;
  fetchWorkspaceMembershipAvailabilities?: (workspaceId: string) => Promise<void>;
  fetchGeoCoords?: (workspaceId: string, membershipIds: string[]) => Promise<{ lat: number; lng: number } | null>;
  fetchFeedPermissions?: (feedId: string) => Promise<void>;
  getWorkspaceRoleLabel?: (role: string) => string;
};

//create a context, with createContext api
export const DataContext = createContext<DataState>({
  preferredLanguage: "en",
  invalidWorkspace: false,
});

type Props = {
  children: React.ReactNode | React.ReactNode[];
};

const DataProvider = ({ children }: Props) => {
  const { user, workspaceMembership } = useContext(UserContext);
  const urlParams = useParams();
  const { pubSub } = React.useContext(AppContext);
  const { limitedMemberRole } = useGlobalFlags();
  const configsDelivered = useSyncExternalStore(subscribeWorkspaceConfigs, workspacesDelivered);
  const amplitudeSessionTracking = true;
  const { currentFeedId } = useContext(CurrentFeedContext);
  const [invalidWorkspace, setInvalidWorkspace] = useState<boolean>(false);
  const [bootstrapComplete, setBootstrapComplete] = useState<boolean>(false);
  const [bootstrapFailed, setBootstrapFailed] = useState<boolean>(false);
  const [firstBootWorkspaceId, setFirstBootWorkspaceId] = useState<string | null>(null);

  const { getLocalStorage, setLocalStorage } = UseStorage();

  const { myAccount } = React.useContext(MyAccountContext);
  const { currentWorkspaceId, workspaces } = React.useContext(WorkspaceContext);
  const telemetryContext = React.useContext(TelemetryContext);
  const deviceKey = `deviceRegistrationId-${currentWorkspaceId}`;
  const [deviceRegistrationId, setDeviceRegistrationId] = useSafeLocalStorage(deviceKey, "");

  const localStoragePreferredLanguage = getLocalStorage("preferredLanguage") as PreferredLanguage;

  const preferredLanguage = localStoragePreferredLanguage || LANGUAGE_DEFAULT_VALUE;

  const setPreferredLanguage = useCallback(
    async (language: PreferredLanguage) => {
      if (!myAccount?.id) return;
      await resetAllItemsLoadedContent();
      await setPreferredLanguageRepo(myAccount.id, language).then(() => {
        setLocalStorage({ key: LANGUAGE_SESSION_KEY, value: language });
      });
    },
    [myAccount?.id, setLocalStorage],
  );

  useEffect(() => {
    //  handsFreeEnabled: handsFreeEnabledForWorkspace(currentWorkspaceId),
    appContext.selectedWorkspaceId = currentWorkspaceId;
    appContext.workspaces = workspaces?.map((w) => {
      return {
        //  handsFreeEnabled: handsFreeEnabledForWorkspace(w?.id) || false,
        workspaceId: w?.id || "",
        feedSelected: w?.id === currentWorkspaceId ? currentFeedId : undefined,
        languageSelected: preferredLanguage,
      };
    });
  }, [currentWorkspaceId, workspaces, currentFeedId, preferredLanguage]);

  useEffect(() => {
    deviceContext.surface = "web";
    deviceContext.surfaceContext = window?.navigator?.userAgent;
    deviceContext.surfaceBuild = buildVersion();
    deviceContext.notificationsEnabled = notificationPermission() === "granted";
    deviceContext.internetConnectionStatus = "online";
    deviceContext.deviceLanguage = window?.navigator?.language || preferredLanguage;
  }, [deviceRegistrationId, preferredLanguage]);

  // setup mapped workspace roles from api -> client label. Both keep one identity across renders: the members and invites
  // tables rebuild their columns when either changes, which remounts every cell and closes an open role menu.
  const availableWorkspaceRoles = useMemo(
    () =>
      new Map<string, MappedWorkspaceRole>([
        ["admin", { role: "admin", label: "Administrator", enabled: true }],
        ["member", { role: "member", label: "Organizer", enabled: true }],
        [
          "limitedMember",
          {
            role: "limitedMember",
            label: "Member",
            enabled: limitedMemberRole,
          },
        ],
      ]),
    [limitedMemberRole],
  );

  const getWorkspaceRoleLabel = useCallback(
    (role: string) => availableWorkspaceRoles?.get(role)?.label || role,
    [availableWorkspaceRoles],
  );

  useEffect(() => {
    if (amplitudeSessionTracking) {
      if (amplitudeSessionTracking) {
        const sessionReplayTracking = sessionReplayPlugin({
          sampleRate: 0.3,
        });
        amplitude.add(sessionReplayTracking);
      }
    }
  }, [amplitudeSessionTracking]);

  const { invalidWorkspaceError, bootstrapPaginatedApplication, allComplete } = useBootstrap();
  const warmupFiredRef = React.useRef(false);
  const driverTranscriptsLangRef = React.useRef<string | null>(null);
  const invitationsHydratedRef = React.useRef<string | null>(null);
  const tmsEventsBootstrappedRef = React.useRef<string | null>(null);

  useEffect(() => {
    setInvalidWorkspace(() => invalidWorkspaceError);
  }, [invalidWorkspaceError]);

  useEffect(() => {
    const accountIds = user?.session?.credentials?.[0].CredentialScopes?.map((c) => c?.accountId);
    const workspaceId = urlParams?.workspaceId ?? null;

    if (!urlParams?.workspaceId || !workspaceId) {
      return;
    }

    // Each workspace boot is one run: the rail indicator follows its stage, and the manifest names the warmup calls
    // the run waits for before it is ready. Loads after that pass through untracked.
    startBootRun({ trigger: "boot", manifest: BOOT_MANIFEST });
    setBootstrapComplete(false);
    setBootstrapFailed(false);
    setFirstBootWorkspaceId(null);
    warmupFiredRef.current = false;
    driverTranscriptsLangRef.current = null;
    invitationsHydratedRef.current = null;
    tmsEventsBootstrappedRef.current = null;

    let stale = false;
    if (accountIds?.length > 0 && workspaceId) {
      bootstrapPaginatedApplication({
        bootstrapItems: bootstrappedApiCalls,
        workspaceId: workspaceId ?? null,
        forceBootstrap: true,
      })
        // A cold bootstrap that failed leaves a partial workspace, so the view shows the failure and offers a fresh
        // boot rather than rendering half a workspace or sitting on `<Loading />` with no way out. Any other
        // rejection (the post-bootstrap pass on an incremental run) keeps the degraded boot of WEB-2245: the view is
        // released and the live data paths start on what the store holds.
        .catch((error) => {
          if (error instanceof ColdBootstrapFailedError) {
            log.error("[BOOTSTRAP] Cold bootstrap failed; showing the failure", { error });
            return "failed" as const;
          }
          log.error("[BOOTSTRAP] Bootstrap failed; continuing with a degraded boot", { error });
          return "degraded" as const;
        })
        .then(async (outcome) => {
          if (stale) return;
          if (outcome === "failed") {
            // The failure screen is the view now; nothing the run still waits for is coming.
            abandonBootRun();
            setBootstrapFailed(true);
            return;
          }
          setFirstBootWorkspaceId(() => workspaceId);
          setBootstrapComplete(() => true);
          subscribeToAppSync({
            accountId: accountIds?.[0],
            currentWorkspaceId: workspaceId,
            pubSub,
            deviceRegistrationId,
          });
          startTagPolling(workspaceId);
        });
    }

    return () => {
      stale = true;
      stopTagPolling();
    };
  }, [user?.session?.credentials?.[0]?.id, urlParams?.workspaceId]);

  useEffect(() => {
    if (!allComplete || !firstBootWorkspaceId) return;
    if (driverTranscriptsLangRef.current === preferredLanguage) return;

    const isLanguageSwitch = driverTranscriptsLangRef.current !== null;
    driverTranscriptsLangRef.current = preferredLanguage;

    const loadDriverTranscripts = async (language: PreferredLanguage) => {
      try {
        const registryKey = `driverTranscripts:${language}`;
        const storedDate = isLanguageSwitch ? undefined : await fetchLatestBootstrapEventDate(registryKey);
        const storedTimestamp = storedDate ? new Date(storedDate).toISOString() : undefined;
        // The cursor the run leaves: the server's clock as its first page goes out, so a transcript revised while the
        // pages were read is inside the next delta.
        const startedAt = serverNow();

        // Match the API ceiling; omit-default is 1k and this call previously relied on the old 50k omit-default.
        const pageSize = BOOTSTRAP_MAX_PAGE_SIZE;
        const maxPages = 50;
        let page = 0;
        let totalItems = 0;
        let totalTranscriptions = 0;
        let effectivePageSize = pageSize;

        let hitMaxPages = false;
        while (page < maxPages) {
          const response = await bootstrapWorkspaceDriverTranscripts(transport, {
            workspaceId: firstBootWorkspaceId,
            language,
            format: "Text",
            timestamp: storedTimestamp,
            page,
            pageSize: effectivePageSize,
          });

          if (typeof response.pageSize === "number" && response.pageSize < effectivePageSize) {
            effectivePageSize = response.pageSize;
          }

          if (response.items?.length) {
            await bulkUpsertItemAndCalculateMembership(response.items, {});
            totalItems += response.items.length;
          }
          if (response.transcriptions?.length) {
            await bulkUpsertWsTranscriptions(response.transcriptions);
            totalTranscriptions += response.transcriptions.length;
          }
          if (response.files?.length) {
            await bulkUpsertFile(response.files);
          }

          if ((response.items?.length ?? 0) < effectivePageSize) {
            break;
          }
          page += 1;
          if (page >= maxPages) {
            hitMaxPages = true;
          }
        }

        if (hitMaxPages) {
          log.warn("[DRIVER_TRANSCRIPTS] Hit max page limit; not marking sync complete", {
            workspaceId: firstBootWorkspaceId,
            maxPages,
            pageSize: effectivePageSize,
          });
          return;
        }

        await insertBootstrapEvent(registryKey, "transcription", startedAt);

        log.info("[DRIVER_TRANSCRIPTS] Loaded driver transcripts", {
          items: totalItems,
          transcriptions: totalTranscriptions,
          incremental: !!storedTimestamp,
        });
      } catch (err) {
        log.warn("[DRIVER_TRANSCRIPTS] Failed to load driver transcripts", err);
      }
    };

    // The endpoint matches a language exactly and a message carries one dialect of it (`es` or `es-MX`), so each
    // dialect of the reader's language loads in turn, under its own registry cursor.
    const loadDriverTranscriptFamily = async () => {
      for (const language of languageFamily(preferredLanguage)) {
        await loadDriverTranscripts(language);
      }
    };

    void runBootTask("warming", "driverTranscripts", loadDriverTranscriptFamily());
  }, [allComplete, firstBootWorkspaceId, preferredLanguage]);

  useEffect(() => {
    if (!allComplete || !firstBootWorkspaceId) return;
    if (invitationsHydratedRef.current === firstBootWorkspaceId) return;
    invitationsHydratedRef.current = firstBootWorkspaceId;

    let stale = false;

    const hydrateInvitations = async () => {
      try {
        const resp = await getWorkspaceInvitations(transport, { workspaceId: firstBootWorkspaceId });
        if (stale) return;
        if (resp?.directInvitations?.length) {
          await bulkUpsertDirectWsInvitation(resp.directInvitations);
        }
        log.info("[INVITATIONS] Hydrated workspace invitations", {
          directInvitations: resp?.directInvitations?.length ?? 0,
        });
      } catch (err) {
        log.warn("[INVITATIONS] Failed to hydrate workspace invitations", err);
      }
    };

    void runBootTask("warming", "invitations", hydrateInvitations());

    return () => {
      stale = true;
    };
  }, [allComplete, firstBootWorkspaceId]);

  // Recent TMS event history for every stream, once per workspace boot, for a workspace on the Dispatch page; from
  // there a poll every minute walks each stream's delta, and a ping brings a walk forward. The board's current routes
  // sync the same way, every minute. Read after boot completes, when the workspace's config is in the store.
  useEffect(() => {
    if (!allComplete || !firstBootWorkspaceId || !myAccount?.id) return;
    const dispatch = getWorkspaceConfig(firstBootWorkspaceId)?.web?.dispatch;
    if (dispatch?.enabled !== true) {
      // Not a Dispatch workspace: the boot does not wait for these.
      for (const name of ["tmsEvents", "currentRoutes", "acknowledgements", "tasks"]) skipBootTask("warming", name);
      return;
    }
    const ctx = { workspaceId: firstBootWorkspaceId, accountId: myAccount.id };
    if (tmsEventsBootstrappedRef.current !== firstBootWorkspaceId) {
      tmsEventsBootstrappedRef.current = firstBootWorkspaceId;
      void runBootTask("warming", "tmsEvents", bootstrapTmsEventStreams(ctx));
      void runBootTask("warming", "currentRoutes", syncCurrentRoutes(ctx));
    }
    const stopTmsEvents = startTmsEventPollingInBrowser(ctx);
    const stopCurrentRoutes = startCurrentRoutesPollingInBrowser(ctx);
    // The acknowledgement log's first run without a cursor is its bootstrap: recent rows first, then the lookback,
    // and the boot waits for both.
    const acknowledgements = startAcknowledgementSyncInBrowser(ctx);
    void runBootTask("warming", "acknowledgements", acknowledgements.bootstrapped);
    // The task logs sync only while the workspace shows tasks; otherwise nothing reaches the task routes.
    const tasks = dispatch.tasks === true ? startTaskSyncInBrowser(ctx) : null;
    if (tasks) void runBootTask("warming", "tasks", tasks.bootstrapped);
    else skipBootTask("warming", "tasks");
    return () => {
      stopTmsEvents();
      stopCurrentRoutes();
      acknowledgements.stop();
      tasks?.stop();
    };
  }, [allComplete, firstBootWorkspaceId, myAccount?.id]);

  useEffect(() => {
    if (!allComplete || !workspaceMembership || !firstBootWorkspaceId) return;
    if (warmupFiredRef.current) return;
    // Wait for the workspace's own `web.warmup`: before the API delivers it, an absent config means "not loaded yet".
    if (!configsDelivered) return;
    // Wait for the account too: the walk is keyed on it, and firing without it would skip warmup for the session.
    const accountId = workspaceMembership.accountId ?? myAccount?.id;
    if (!accountId) return;
    warmupFiredRef.current = true;

    const settings = warmupSettingsFor(firstBootWorkspaceId);
    if (settings.mode === "off") {
      log.info("[WARMUP] Feed warmup is off for this workspace", { workspaceId: firstBootWorkspaceId });
      skipBootTask("warming", "feedWarmup");
      return;
    }

    // The Dispatch page counts each driver's unread messages from the store, so a `limited` pick widens to every
    // driver channel. The same walk serves the classic and the Dispatch view, through one log.
    const dispatchEnabled = getWorkspaceConfig(firstBootWorkspaceId)?.web?.dispatch?.enabled === true;

    log.info("[WARMUP] Initiating feed warmup after bootstrap", {
      workspaceId: firstBootWorkspaceId,
      membershipId: workspaceMembership.id,
      ...settings,
      dispatchEnabled,
    });

    runBootTask(
      "warming",
      "feedWarmup",
      warmupFeedItems({
        workspaceId: firstBootWorkspaceId,
        accountId,
        membershipId: workspaceMembership.id,
        settings,
        dispatchEnabled,
        trackAction: telemetryContext?.trackAction,
        finishAction: telemetryContext?.finishAction,
      }),
    )
      .then((stats) => {
        log.info("[WARMUP] Feed warmup completed successfully", stats);
      })
      .catch((err) => {
        log.warn("[WARMUP] Feed warmup failed but continuing", err);
      });
  }, [allComplete, firstBootWorkspaceId, workspaceMembership?.id, configsDelivered, myAccount?.id]);

  const fetchWorkspaceMembership = async (accountId: string, workspaceId: string) => {
    log.info("fetchWorkspaceMembership", accountId, workspaceId);
    const workspaceMembershipRecord = rowsByIndex("workspaceMembership", idx.membershipsByAccount, accountId).find(
      (membership) => membership.workspaceId === workspaceId && membership.status === "active",
    );
    if (!workspaceMembershipRecord) {
      return Promise.reject("Invalid Workspace");
    }
    return workspaceMembershipRecord;
  };

  const listFeedPermissions = React.useCallback(
    async (workspaceMembershipId: string, feedId: string): Promise<Array<Permission>> => {
      log.info("listFeedPermissions", workspaceMembershipId, feedId);
      return rowsByIndex("permission", idx.permissionsByFeed, feedId).filter(
        (permission) => permission.workspaceMembershipId === workspaceMembershipId,
      );
    },
    [],
  );

  const fetchWorkspaceMembershipAvailabilities = async (workspaceId: string): Promise<void> => {
    await syncWorkspaceMembershipAvailabilities(workspaceId);
  };

  const fetchGeoCoords = async (
    workspaceId: string,
    membershipIds: string[],
  ): Promise<{ lat: number; lng: number } | null> => {
    const { data } = await getLocations(transport, { workspaceId, membershipIds });
    return data[0]?.locations[0]?.latitude
      ? {
          lat: data[0]?.locations[0]?.latitude,
          lng: data[0]?.locations[0]?.longitude,
        }
      : null;
  };

  const fetchFeedPermissions = async (feedId: string) => {
    await downloadPaginatedFeedPermissions({
      feedId,
      workspaceId: currentWorkspaceId,
    });
    return;
  };

  useEffect(() => {
    const f = async () => {
      try {
        let deviceId = deviceRegistrationId;
        if (!deviceId) {
          deviceId = cuid();
        }

        // prevent double quotes from entering the DB
        const cleanDeviceId = deviceId.replace(/"/g, "");

        const newDeviceReg = {
          id: cleanDeviceId,
          surface: "web",
        } as DeviceRegistration;
        const payload = {
          deviceRegistration: newDeviceReg,
        };
        // @ts-ignore
        const deviceRegistration = await registerDevice(transport, payload);
        if (deviceRegistration?.id) {
          setDeviceRegistrationId(deviceRegistration.id);
        }
      } catch (error) {
        log.error("Error registering device", error);
      }
    };
    f();
  }, []);

  const dataState: DataState = {
    availableWorkspaceRoles,
    getWorkspaceRoleLabel,
    bootstrapComplete,
    bootstrapFailed,
    firstBootWorkspaceId,
    preferredLanguage,
    setPreferredLanguage: setPreferredLanguage,
    fetchWorkspaceMembership,
    listFeedPermissions,
    fetchWorkspaceMembershipAvailabilities,
    fetchGeoCoords,
    loadWorkspaceWorkflowItems,
    invalidWorkspace,
    setInvalidWorkspace,
    fetchFeedPermissions,
  };
  return (
    <DataContext.Provider value={dataState}>
      {bootstrapFailed && <BootstrapFailedModal open={true} />}
      {children}
    </DataContext.Provider>
  );
};
export default DataProvider;
